/**
 * @file ProfilePage.js
 * @author Jonas Matulis, Simon Tenedero
 * @created 2024-XX-XX
 * @lastModified 2024-XX-XX
 * @description file containing the ProfilePage component.
 */
import { useEffect, useState, useRef } from "react";
import {
  updateDisplayName,
  updatePfp,
  uploadUserProfilePhoto,
  updateBio, // You'll need to add this function to usersUtils
} from "../utils/usersUtils";
import { useUser } from "../context/UserContext";

/**
 * This page allows users to manage their account settings, including changing their profile picture and display name.
 * It provides functionality to upload a new profile picture, reset the profile picture to the original one,
 * change the display name, and reset the display name to the username.
 *
 * @returns {JSX.Element} The rendered Profile page
 */
const ProfilePage = () => {
  // State variables managing pfp, name, bio, and selected file
  const [newPfp, setNewPfp] = useState(null);
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null); // Ref for file input

  // Success message states
  const [pfpSuccessMessage, setPfpSuccessMessage] = useState("");
  const [nameSuccessMessage, setNameSuccessMessage] = useState("");
  const [bioSuccessMessage, setBioSuccessMessage] = useState("");
  const [loading, setLoading] = useState({
    upload: false,
    changeName: false,
    changeBio: false,
  });

  const { user, setUser } = useUser();

  // Change name, pfp, and bio if user is changed
  useEffect(() => {
    if (user) {
      setName(user.username);
      setNewPfp(user.profilePicture);
      setBio(user.bio || "");
    }
  }, [user]);

  // Clear success messages after timeout
  useEffect(() => {
    if (pfpSuccessMessage) {
      const timer = setTimeout(() => setPfpSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [pfpSuccessMessage]);

  useEffect(() => {
    if (nameSuccessMessage) {
      const timer = setTimeout(() => setNameSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [nameSuccessMessage]);

  useEffect(() => {
    if (bioSuccessMessage) {
      const timer = setTimeout(() => setBioSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [bioSuccessMessage]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPfp(reader.result);
      };
      reader.readAsDataURL(file);
      setSelectedFile(file);

      // Clear any previous success messages when new file is selected
      setPfpSuccessMessage("");
    }
  };

  const handleUploadClick = async () => {
    try {
      if (newPfp && selectedFile) {
        setLoading((prev) => ({ ...prev, upload: true }));

        // Update local user object, we don't want to rely on the userContext listener to handle this, because it'll cause a re-render of all consumers (too expensive!)
        user.profilePicture = newPfp;
        setUser(user);

        const profilePhoto = await uploadUserProfilePhoto(
          user.uid,
          selectedFile,
        );
        await updatePfp(user.uid, profilePhoto);

        // Show success message
        setPfpSuccessMessage("Profile picture uploaded successfully!");
        setSelectedFile(null); // Clear selected file
      }
    } catch (error) {
      console.log("Error uploading new pfp...");
      setPfpSuccessMessage(
        "Error uploading profile picture. Please try again.",
      );
      throw error;
    } finally {
      setLoading((prev) => ({ ...prev, upload: false }));
    }
  };

  const handleCancelPhotoChange = () => {
    // Reset to original profile picture
    setNewPfp(user.profilePicture);
    setSelectedFile(null);
    setPfpSuccessMessage("");
  };

  const handleResetName = async () => {
    try {
      setLoading((prev) => ({ ...prev, changeName: true }));

      // Update local user object, we don't want to rely on the userContext listener to handle this, because it'll cause a re-render of all consumers (too expensive!)
      user.username = user.displayName || "";
      setUser(user);
      setName(user.username); //preview name

      await updateDisplayName(user.uid, user.displayName || "");

      setNameSuccessMessage("Profile name reset successfully!");
    } catch (error) {
      setNameSuccessMessage("Error resetting profile name. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, changeName: false }));
    }
  };

  const handleNameChange = async (e) => {
    e.preventDefault();

    try {
      setLoading((prev) => ({ ...prev, changeName: true }));

      // Update local user object, we don't want to rely on the userContext listener to handle this, because it'll cause a re-render of all consumers (too expensive!)
      user.username = name;
      setUser(user);
      setName(name);

      await updateDisplayName(user.uid, user.username);

      setNameSuccessMessage("Profile name changed successfully!");
    } catch (error) {
      setNameSuccessMessage("Error changing profile name. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, changeName: false }));
    }
  };

  const handleBioChange = async (e) => {
    e.preventDefault(); // Only prevent default if event exists

    try {
      setLoading((prev) => ({ ...prev, changeBio: true }));

      //Update the user object with spread operator to create new object
      user.bio = bio;
      setUser(user);

      setBio(bio);
      await updateBio(user.uid, bio);

      setBioSuccessMessage("Bio updated successfully!");
    } catch (error) {
      setBioSuccessMessage("Error updating bio. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, changeBio: false }));
    }
  };

  const handleResetBio = async () => {
    try {
      setLoading((prev) => ({ ...prev, changeBio: true }));

      const originalBio = user.bio; // fetch original bio

      user.bio = originalBio;
      setUser(user);

      setBio(originalBio);
    } catch (error) {
      setBioSuccessMessage("Error resetting bio. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, changeBio: false }));
    }
  };

  const handlePreviewClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="min-h-screen py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-white">
            Customize Profile
          </h1>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Current Profile Display */}
          <div className="space-y-6 flex flex-col justify-center">
            {/* Current Profile Picture */}
            <div className="p-6 sm:p-8 rounded-lg">
              <h2 className="text-center text-2xl md:text-4xl text-white mb-4">
                {"@" + name || "No name set"}
              </h2>

              <div className="text-center mb-6">
                <div className="relative inline-block">
                  <img
                    src={newPfp || ""}
                    alt="Profile"
                    className="w-[200px] h-[200px] md:w-[300px] md:h-[300px] rounded-full object-cover mx-auto border-2 border-gray-200 cursor-pointer hover:border-blue-400 hover:bg-gray-200 transition-all"
                    onClick={handlePreviewClick}
                    title={"click to select profile photo"}
                  />
                  {selectedFile && (
                    <button
                      onClick={handleCancelPhotoChange}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold hover:bg-red-600 transition-colors z-10"
                      title="Cancel photo change"
                    >
                      ×
                    </button>
                  )}
                  {loading.upload && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                      <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>

                {/* Instructional text */}
                <p className="text-gray-400 text-sm mt-2">
                  Change profile photo by pressing icon above
                </p>

                {/* Upload Button - Show right under the photo when file is selected */}
                {selectedFile && (
                  <div className="mt-4">
                    <button
                      onClick={handleUploadClick}
                      disabled={loading.upload}
                      className="bg-blue-500 text-white font-medium py-3 px-6 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading.upload ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Uploading...
                        </div>
                      ) : (
                        "Change Profile Photo"
                      )}
                    </button>
                  </div>
                )}

                {/* Hidden File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />

                {/* Success/Error Message for Profile Picture */}
                {pfpSuccessMessage && (
                  <div
                    className={`mt-4 mb-2 p-3 rounded-lg text-sm ${
                      pfpSuccessMessage.includes("Error")
                        ? "bg-red-50 text-red-700 border border-red-200"
                        : "bg-green-50 text-green-700 border border-green-200"
                    }`}
                  >
                    {pfpSuccessMessage}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Edit Options */}
          <div className="space-y-6">
            {/* Profile Name Section */}
            <div className="p-6 sm:p-8rounded-lg">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">
                Change Username
              </h3>

              <div>
                {/* Name Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 text-gray-300">
                    This is what other users of the platform will know you as.
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      // Clear success message when user starts typing
                      if (nameSuccessMessage) setNameSuccessMessage("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleNameChange(e);
                      }
                    }}
                    placeholder="Enter your display name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Success/Error Message for Name */}
                {nameSuccessMessage && (
                  <div
                    className={`mb-4 p-3 rounded-lg text-sm ${
                      nameSuccessMessage.includes("Error")
                        ? "bg-red-50 text-red-700 border border-red-200"
                        : "bg-green-50 text-green-700 border border-green-200"
                    }`}
                  >
                    {nameSuccessMessage}
                  </div>
                )}

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleNameChange}
                    disabled={!name.trim() || loading.changeName}
                    className="flex-1 bg-primary text-white font-medium py-3 px-4 rounded-lg hover:bg-accent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading.changeName ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Changing...
                      </div>
                    ) : (
                      "Change Username"
                    )}
                  </button>

                  <button
                    onClick={handleResetName}
                    disabled={loading.changeName}
                    className="flex-1 bg-gray-600 text-white font-medium py-3 px-4 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading.changeName ? "Processing..." : "Reset Name"}
                  </button>
                </div>
              </div>
            </div>

            {/* Bio Section */}
            <div className="mt-6 p-4">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">
                Biography
              </h3>

              {/* Bio Display/Input */}
              <div className="mb-2">
                <label className="block text-sm font-medium mb-2 text-gray-300">
                  Tell users more about yourself! (max 200 characters)
                </label>

                <textarea
                  value={bio}
                  onChange={(e) => {
                    setBio(e.target.value);
                    // Clear success message when user starts typing
                    if (bioSuccessMessage) setBioSuccessMessage("");
                  }}
                  placeholder="write your bio here..."
                  rows={4}
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                />
                <div className="text-right text-xs text-gray-400 mt-1">
                  {bio.length}/200
                </div>
              </div>

              {/* Success/Error Message for Bio */}
              {bioSuccessMessage && (
                <div
                  className={`mb-4 p-3 rounded-lg text-sm ${
                    bioSuccessMessage.includes("Error")
                      ? "bg-red-50 text-red-700 border border-red-200"
                      : "bg-green-50 text-green-700 border border-green-200"
                  }`}
                >
                  {bioSuccessMessage}
                </div>
              )}

              {/* Bio Buttons */}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleBioChange}
                  disabled={loading.changeBio}
                  className="flex-1 bg-primary text-white font-medium py-2 px-4 rounded-lg hover:bg-accent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  {loading.changeBio ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Updating...
                    </div>
                  ) : (
                    "Update Bio"
                  )}
                </button>

                <button
                  onClick={handleResetBio}
                  disabled={loading.changeBio}
                  className="flex-1 bg-gray-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  {loading.changeBio ? "Processing..." : "Reset Bio"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
